import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { sendGTMEvent } from 'reduxModules/googleTagManager/action';
import {
  COURSE_LESSON_CLICK,
  COURSE_LESSONPAGE_LESSON_CLICK,
  COURSE_PROMOTE_CLICK,
  COURSE_LESSON_END,
  COURSE_PROMOTE_END,
  COURSE_LESSON_PLAY,
  COURSE_PROMOTE_PLAY,
} from 'constants/gtmEvent';

import usePrevious from 'hooks/usePrevious';

const LESSON_TYPE = {
  NORMAL: 'NORMAL',
  PROMOTE: 'PROMOTE',
  LESSON_PREVIEW: 'LESSON_PREVIEW',
};

const INIT = {
  freeLesson: undefined,
  lessonId: undefined,
  lessonName: undefined,
  lessonDuration: undefined,
  lessonMaterialNum: undefined,
  lessonIndex: undefined,
  lessonType: undefined,
  timestampNum: undefined,
  startTime: undefined,
  endTime: undefined,
  firstPlay: undefined,
  autoPlay: undefined,
  progress: undefined,
  isStartPlay: false,
  isEnded: false,
  isRated: false,
};

const VIDEO_EVENT_NAME = {
  [true]: {
    CLICK: COURSE_PROMOTE_CLICK,
    PLAY: COURSE_PROMOTE_PLAY,
    END: COURSE_PROMOTE_END,
  },
  [false]: {
    CLICK: COURSE_LESSON_CLICK,
    LESSONPAGE_CLICK: COURSE_LESSONPAGE_LESSON_CLICK,
    PLAY: COURSE_LESSON_PLAY,
    END: COURSE_LESSON_END,
  },
};

const getLessonType = ({ isTrailer, isFree }) => {
  if (isTrailer) {
    return LESSON_TYPE.PROMOTE;
  }
  return isFree ? LESSON_TYPE.LESSON_PREVIEW : LESSON_TYPE.NORMAL;
};

export const getProgress = ({ endTime, startTime, lessonDuration }) =>
  (endTime - startTime) / lessonDuration;

export const getLessonEventTrackData = ({ lesson, autoPlay, index }) => {
  const {
    durationTime,
    id,
    isFree,
    isRated,
    isTrailer,
    learningProgress,
    materials = [],
    progressDuration,
    timestamps = [],
    title,
  } = lesson;

  return {
    freeLesson: isFree,
    lessonId: id,
    lessonName: title,
    lessonDuration: durationTime,
    lessonIndex: index,
    lessonMaterialNum: materials.length,
    timestampNum: timestamps.length,
    startTime: progressDuration,
    firstPlay: !progressDuration && !learningProgress,
    autoPlay,
    lessonType: getLessonType({ isTrailer, isFree }),
    isStartPlay: false,
    isEnded: false,
    isRated,
  };
};

const useLessonEventTrack = ({ commonEventTrackData, isCoursePage }) => {
  const dispatch = useDispatch();
  const [lessonEventTrackData, setLessonEventTrackData] = useState(INIT);
  const [lessonPageEventTrackData, setLessonPageEventTrackData] = useState(
    null,
  );
  const [isLessonClicked, setIsLessonClicked] = useState(false);
  const prevLessonEventTrackData = usePrevious(lessonEventTrackData);
  const isTrailer = lessonEventTrackData.lessonType === LESSON_TYPE.PROMOTE;

  useEffect(() => {
    if (!(isLessonClicked && lessonEventTrackData.lessonId)) {
      return;
    }

    const eventTrackData = isCoursePage
      ? {
          ...lessonEventTrackData,
          ...commonEventTrackData,
        }
      : {
          courseId: commonEventTrackData.courseId,
          courseName: commonEventTrackData.courseName,
          ...lessonPageEventTrackData,
        };

    setIsLessonClicked(false);
    dispatch(
      sendGTMEvent({
        eventName: isCoursePage
          ? VIDEO_EVENT_NAME[isTrailer].CLICK
          : VIDEO_EVENT_NAME[isTrailer].LESSONPAGE_CLICK,
        data: eventTrackData,
      }),
    );
  }, [
    isLessonClicked,
    lessonEventTrackData,
    lessonPageEventTrackData,
    commonEventTrackData,
    dispatch,
    isTrailer,
    isCoursePage,
  ]);

  useEffect(() => {
    const sentEventTrack = (eventName, keyProp, sendGTMEventProps = {}) => {
      dispatch(
        sendGTMEvent({
          eventName,
          data: {
            ...prevLessonEventTrackData,
            ...commonEventTrackData,
            ...sendGTMEventProps,
          },
        }),
      );
      setLessonEventTrackData(state => ({
        ...state,
        [keyProp]: false,
      }));
    };

    if (lessonEventTrackData.isEnded) {
      const { lessonDuration, startTime } = lessonEventTrackData;
      sentEventTrack(VIDEO_EVENT_NAME[isTrailer].END, 'isEnded', {
        progress: getProgress({
          startTime,
          endTime: lessonDuration,
          lessonDuration,
        }),
      });
    }
    if (lessonEventTrackData.isStartPlay) {
      sentEventTrack(VIDEO_EVENT_NAME[isTrailer].PLAY, 'isStartPlay');
    }
  }, [
    dispatch,
    prevLessonEventTrackData,
    lessonEventTrackData,
    commonEventTrackData,
    isTrailer,
  ]);

  return {
    lessonEventTrackData: {
      ...lessonEventTrackData,
      ...commonEventTrackData,
    },
    setLessonEventTrackData,
    setLessonPageEventTrackData,
    setIsLessonClicked,
  };
};

export default useLessonEventTrack;
